import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ViewMode = 'compact' | 'comfortable';
export type TableDensity = 'dense' | 'normal' | 'relaxed';

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  tableDensity: TableDensity;
  setTableDensity: (density: TableDensity) => void;
  isMobile: boolean;
}

const ViewModeContext = createContext<ViewModeContextType | null>(null);

export function ViewModeProvider({ children }: { children: ReactNode }) {
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('argocamp-view-mode');
    return (saved as ViewMode) || 'compact';
  });

  const [tableDensity, setTableDensityState] = useState<TableDensity>(() => {
    const saved = localStorage.getItem('argocamp-table-density');
    return (saved as TableDensity) || 'normal';
  });

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
    localStorage.setItem('argocamp-view-mode', mode);
  };

  const setTableDensity = (density: TableDensity) => {
    setTableDensityState(density);
    localStorage.setItem('argocamp-table-density', density);
  };

  return (
    <ViewModeContext.Provider value={{ 
      viewMode, 
      setViewMode, 
      tableDensity, 
      setTableDensity,
      isMobile 
    }}>
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode() {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('useViewMode must be used within ViewModeProvider');
  }
  return context;
}

